import React, { useMemo, useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Search as SearchIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import PageHero from "@/components/site/PageHero";
import BottomCTA from "@/components/site/BottomCTA";
import useReveal from "@/hooks/useReveal";
import { searchIndex } from "@/data/searchIndex";
import { pickLocale } from "@/i18n";

const pick = (item, key) => item[`${key}_vi`] || item[key] || "";

export default function SearchResults() {
  useReveal();
  const { t, i18n } = useTranslation();
  const [params, setParams] = useSearchParams();
  const initialQ = params.get("q") || "";
  const [query, setQuery] = useState(initialQ);

  // Keep local input in sync when the URL query param changes (deep links / browser back)
  useEffect(() => {
    setQuery(initialQ);
  }, [initialQ]);

  const lang = i18n.language?.startsWith("vi") ? "vi" : "en";
  const results = useMemo(() => searchIndex(initialQ, lang), [initialQ, lang]);

  const submit = (e) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    setParams({ q });
  };

  return (
    <div className="min-h-screen bg-white text-unik-ink">
      <SiteNav />

      <PageHero
        eyebrow={t("search.eyebrow")}
        title={t("search.heroTitle")}
        accent={t("search.heroAccent")}
        sub={
          initialQ
            ? t("search.resultsFor", { q: initialQ, count: results.length })
            : t("search.heroSub")
        }
        bgImage="/assets/unsplash-1583417319070.jpg"
      />

      <section className="relative bg-white pt-16 lg:pt-20">
        <div className="mx-auto max-w-[900px] px-6 lg:px-10">
          {/* Search input */}
          <form
            onSubmit={submit}
            className="relative"
            data-testid="search-form"
          >
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("search.placeholder")}
              data-testid="search-input"
              autoFocus
              className="w-full bg-[#f3f4f6] border border-[#14355a]/15 text-[#14355a] placeholder-[#14355a]/40 rounded-full pl-6 pr-16 py-4 text-[15px] lg:text-[16px] font-display focus:outline-none focus:border-unik-gold transition-colors"
            />
            <button
              type="submit"
              data-testid="search-submit"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-11 h-11 bg-[#14355a] hover:bg-unik-gold text-white rounded-full flex items-center justify-center transition-colors"
              aria-label={t("common.search")}
            >
              <SearchIcon size={18} />
            </button>
          </form>
        </div>
      </section>

      <section className="relative bg-white pt-14 lg:pt-16 pb-28 lg:pb-36">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
          {initialQ && results.length === 0 ? (
            <div
              data-reveal
              className="text-center py-20"
              data-testid="search-empty"
            >
              <div className="eyebrow text-[#14355a]/50">{t("search.noResultsEyebrow")}</div>
              <h3
                style={{ fontSize: "clamp(1.5rem, 2.5vw, 2.25rem)", fontWeight: 500 }}
                className="mt-4 font-display text-[#14355a] tracking-tight"
              >
                {t("search.noResults", { q: initialQ })}
              </h3>
              <p className="mt-6 max-w-md mx-auto text-[15px] leading-[1.7] text-unik-ink/65">
                {t("search.noResultsHint")}
              </p>
            </div>
          ) : null}

          {results.length > 0 && (
            <div
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
              data-testid="search-results-grid"
            >
              {results.map((r, i) => (
                <Link
                  key={`${r.to}-${i}`}
                  to={r.to}
                  data-reveal
                  data-testid={`search-result-${r.type}-${i}`}
                  style={{ "--reveal-delay": `${(i % 6) * 70}ms` }}
                  className="group block bg-[#f3f4f6] hover:bg-white border border-[#14355a]/8 hover:border-[#14355a]/25 transition-all duration-500 overflow-hidden"
                >
                  {r.img ? (
                    <div className="aspect-[16/10] overflow-hidden bg-[#14355a]/5">
                      <img
                        src={r.img}
                        alt={pick(r, "title")}
                        className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div className="aspect-[16/10] bg-gradient-to-br from-[#14355a] to-[#14355a]/70 flex items-center justify-center">
                      <SearchIcon size={28} className="text-white/40" />
                    </div>
                  )}
                  <div className="p-6 lg:p-7">
                    <div className="eyebrow text-unik-gold">
                      {pickLocale(r, "typeLabel") || r.typeLabel}
                    </div>
                    <h3
                      style={{
                        fontSize: "clamp(1rem, 1.2vw, 1.25rem)",
                        fontWeight: 500,
                        lineHeight: 1.3,
                      }}
                      className="mt-3 font-display text-[#14355a] tracking-tight"
                    >
                      {pickLocale(r, "title") || r.title}
                    </h3>
                    <p className="mt-3 text-[13.5px] leading-[1.7] text-unik-ink/65 line-clamp-3">
                      {pickLocale(r, "description") || r.description}
                    </p>
                    <span className="inline-flex items-center gap-2 mt-5 text-[#14355a] text-[11px] uppercase tracking-[0.22em] font-display font-medium group-hover:text-unik-gold transition-colors">
                      {t("search.visitPage")}
                      <ArrowRight
                        size={13}
                        className="transition-transform duration-500 group-hover:translate-x-1"
                      />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {!initialQ && results.length === 0 ? (
            <div
              data-reveal
              className="text-center py-20"
              data-testid="search-idle"
            >
              <div className="eyebrow text-[#14355a]/50">{t("search.idleEyebrow")}</div>
              <p className="mt-6 max-w-md mx-auto text-[15px] leading-[1.7] text-unik-ink/65">
                {t("search.idleHint")}
              </p>
            </div>
          ) : null}
        </div>
      </section>

      <BottomCTA
        eyebrow={t("insights.bottomCta.eyebrow")}
        title={t("insights.bottomCta.title")}
        accent={t("insights.bottomCta.accent")}
        body={t("insights.bottomCta.body")}
        primary={{ label: t("insights.bottomCta.primary"), to: "/contact" }}
        secondary={{ label: t("nav.about"), to: "/about/who-we-are" }}
      />

      <SiteFooter />
    </div>
  );
}
